import { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import ptBR from 'antd/locale/pt_BR';
import { DarwinFlex } from '../../Flex';
import { DarwinTable, DTableProps } from '../../Table';
import { DarwinPagination } from '../../Pagination';
import { useResizableColumns } from '../hooks';
import ListViewHeader from '../ListViewHeader';
import ResizableTitle from '../ResizableTitle';
import { DarwinListProps, RowClassName } from '../types';
import styles from './index.module.css';

const getRowClassName = (_: unknown, index: number) =>
  index % 2 === 0 ? styles.evenRow : '';

const antdLocales = {
  en: enUS,
  es: esES,
  pt: ptBR,
};

export const DarwinListView = <T,>(props: DarwinListProps<T>) => {
  const { t } = useTranslation();

  const {
    data = [],
    columns = [],
    pagination,
    isLoading,
    emptyText,
    paddingClassName,
    header,
    table,
    behavior,
  } = props;

  const effectiveEmptyText = emptyText ?? t('common-table.empty-search');

  // Use grouped props with defaults
  const onRowClick = behavior?.onRowClick;
  const onChange = table?.onChange;
  const customRowClassName = table?.customRowClassName;
  const placeholderWidth = header?.placeholderWidth ?? 400;
  const scrollable = table?.scrollable ?? true;
  const resizable = table?.resizable ?? true;
  const isRowInactive = behavior?.isRowInactive;
  const rowKey = table?.rowKey ?? 'key';
  const setSearchQuery = header?.setSearchQuery;
  const searchPlaceholder = header?.searchPlaceholder;
  const title = header?.title;
  const customHeaderButton = header?.actions;
  const { mergedColumns } = useResizableColumns(columns, resizable);

  const {
    pageSize = 10,
    currentPage,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 30, 50],
    total,
  } = pagination || {};

  const [maintainedTotal, setMaintainedTotal] = useState(total);

  useEffect(() => {
    if (total !== undefined) {
      setMaintainedTotal(total);
    }
  }, [total]);

  const rowClassName = useCallback<RowClassName<T>>(
    (record, index, indent = 0) => {
      const classes: string[] = [];
      const baseRowClass = getRowClassName(record, index);
      if (baseRowClass) classes.push(baseRowClass);
      if (onRowClick) classes.push(styles.rowHover);
      if (isRowInactive && isRowInactive(record, index))
        classes.push(styles.rowInactive);
      if (customRowClassName) {
        const custom =
          typeof customRowClassName === 'function'
            ? customRowClassName(record, index, indent)
            : customRowClassName;
        if (custom) classes.push(custom);
      }
      return classes.join(' ');
    },
    [onRowClick, isRowInactive, customRowClassName]
  );

  const onRow = useCallback<NonNullable<DTableProps<T>['onRow']>>(
    (record: T) => ({ onClick: () => onRowClick?.(record) }),
    [onRowClick]
  );

  const tableComponents = useMemo(
    () => (resizable ? { header: { cell: ResizableTitle } } : undefined),
    [resizable]
  );
  const currentAntdLocale =
    antdLocales[i18n.language as keyof typeof antdLocales] || enUS;

  const tableLocale = useMemo(
    () => ({
      ...currentAntdLocale.Table,
      emptyText: isLoading ? t('common-table.loading') : effectiveEmptyText,
    }),
    [currentAntdLocale, effectiveEmptyText, isLoading, t]
  );
  const tableScroll = useMemo(
    () => (scrollable ? { x: 'max-content' } : undefined),
    [scrollable]
  );

  return (
    <DarwinFlex
      flex={1}
      className={`${styles.tableContainer} ${
        paddingClassName ?? styles.tableContainerPadding
      }`}
    >
      <ListViewHeader
        title={title}
        setSearchQuery={setSearchQuery}
        searchPlaceholder={searchPlaceholder}
        actions={customHeaderButton}
        placeholderWidth={placeholderWidth}
      />

      <DarwinFlex vertical flex={1} justify="space-between">
        <div>
          <DarwinTable<T>
            loading={isLoading}
            rowClassName={rowClassName}
            columns={mergedColumns}
            dataSource={data}
            pagination={false}
            onChange={onChange}
            onRow={onRow}
            rowKey={rowKey}
            scroll={tableScroll}
            locale={tableLocale}
            components={tableComponents}
            bordered
          />
        </div>

        {pagination && (
          <DarwinFlex justify="end" align="center">
            <DarwinPagination
              onChange={onPageChange}
              total={maintainedTotal}
              current={currentPage}
              pageSize={pageSize}
              showSizeChanger
              onShowSizeChange={onPageSizeChange}
              pageSizeOptions={pageSizeOptions}
            />
          </DarwinFlex>
        )}
      </DarwinFlex>
    </DarwinFlex>
  );
};
